import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import last from 'lodash/last';
import head from 'lodash/head';

import * as d3 from 'd3';

import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { linearRegression, linearRegressionLine } from 'simple-statistics';

const canvasMargin = {
  top: 20,
  left: 100,
  right: 20,
  bottom: 20
};

const dotsMargin = {
  top: 0,
  right: 0,

  left: 20,
  bottom: 20
};

const ScatterPlotComponent = ({
  width,
  height,
  dots,
  originalDots,
  showRegressionLine,
  confidenceIntervalDots,
  xAxisLabel = '',
  yAxisLabel = '',

  dotFillColor = 'steelblue',
  lineColor = 'darkorange'
}) => {

  const d3Container = useRef(null);

  const dotsWidthMargin = dotsMargin.left + dotsMargin.right;
  const canvasWidthMargin = canvasMargin.left + canvasMargin.right;
  const widthGraph = width - canvasWidthMargin - dotsWidthMargin;

  const dotsHeightMargin = dotsMargin.top + dotsMargin.bottom;
  const canvasHeightMargin = canvasMargin.top + canvasMargin.bottom;
  const heightGraph = height - canvasHeightMargin - dotsHeightMargin;

  const allDots = originalDots ? [...dots, ...originalDots] : dots;

  const xDomain = d3.extent(map(allDots, head));
  const yDomain = d3.extent(map(allDots, last));

  // create scale objects
  const x = d3.scaleLinear().domain(xDomain).range([0, widthGraph]);
  const y = d3.scaleLinear().domain(yDomain).range([heightGraph, 0]);

  useEffect(() => {
    const root = d3.select(d3Container.current);

    const drawAxisX = () => {
      // create axis objects
      const xTicks = x.ticks();
      const axisBottom = d3
        .axisBottom(x)
        .tickValues(xTicks)
        .tickFormat(value => {
          return Number(value.toFixed(3)).toString();
        });

      const xAxisContainer = root
        .append('g')
        .attr('id', 'x-axis')
        .attr('class', 'dotplot')
        .attr('transform', `translate(${canvasMargin.left + dotsMargin.left}, ${heightGraph + canvasMargin.top + dotsMargin.bottom})`);

      xAxisContainer.append('g').attr('id', 'axisX').style('font-size', '14px').call(axisBottom);

      if (xAxisLabel) {
        xAxisContainer
          .append('text')
          .text(xAxisLabel)
          .attr('class', 'x-axis')
          .attr('dy', '-5')
          .attr('dx', widthGraph)
          .attr('font-size', '16')
          .attr('text-anchor', 'end');
      }
    };

    root.select('#x-axis').remove();
    drawAxisX();
  }, [xAxisLabel, xDomain]);

  useEffect(() => {
    const root = d3.select(d3Container.current);
    const drawAxisY = () => {
      const yAxisContainer = root
        .append('g')
        .attr('id', 'y-axis')
        .attr('class', 'dotplot')
        .attr('transform', `translate(${canvasMargin.left}, ${canvasMargin.top})`);
      const axisLeft = d3.axisLeft(y);
      yAxisContainer.append('g').attr('id', 'axisY').style('font-size', '14px').call(axisLeft);


      if (yAxisLabel) {
        yAxisContainer
          .append('text')
          .text(yAxisLabel)
          .attr('class', 'y-axis')
          .attr('dy', '15')
          .attr('font-size', '16')
          .attr('text-anchor', 'end')
          .attr('transform', 'rotate(-90)');
      }
    };

    root.select('#y-axis').remove();
    drawAxisY();
  }, [yAxisLabel, yDomain]);

  useEffect(() => {
    const root = d3.select(d3Container.current);

    const zoomed = ({ transform }) => {
      const newXScale = transform.rescaleX(x);
      const newYScale = transform.rescaleY(y);

      d3.select('#axisX').call(d3.axisBottom(newXScale));
      d3.select('#axisY').call(d3.axisLeft(newYScale));

      root.select('#dotsContainer')
        .selectAll('circle')
        .attr('cx', d => newXScale(d[0]))
        .attr('cy', d => newYScale(d[1]))
        .attr('visibility', d => {
          if (transform.k > 1) {
            return newXScale(d[0]) < 0 || newYScale(d[1]) < 0 || newXScale(d[0]) > width || newYScale(d[1]) > height ? 'hidden' : '';
          }
          return '';
        });

      const line = d3.line()
        .x(value => newXScale(value[0]) + canvasMargin.left + dotsMargin.left)
        .y(value => newYScale(value[1]) + canvasMargin.top);

      if (showRegressionLine) {
        root.select('#regressionLine').attr('d', line);
      }

      if (confidenceIntervalDots) {
        root.select('#lwrConfidenceInterval').attr('d', line);
        root.select('#uprConfidenceInterval').attr('d', line);
      }
    };

    const drawCircles = () => {
      const dotsContainer = root
        .append('g')
        .attr('id', 'dotsContainer')
        .attr('transform', `translate(${canvasMargin.left + dotsMargin.left}, ${canvasMargin.top})`
        );

      dotsContainer.selectAll('circle')
        .data(allDots)
        .join('circle')
        .attr('r', '2.5')
        .attr('cx', value => x(value[0]))
        .attr('cy', value => y(value[1]))
        .attr('fill', dotFillColor);

      const zoom = d3.zoom()
        .scaleExtent([0.5, 10])
        .extent([[0, 0], [width - canvasMargin.left - dotsMargin.left, height - canvasMargin.top]])
        .translateExtent([[0, 0], [width - canvasMargin.left - dotsMargin.left, height - canvasMargin.top]])
        .on('zoom', zoomed);

      root.call(zoom).call(zoom.transform, d3.zoomIdentity);
    };

    root.select('#dotsContainer').remove();
    drawCircles();
  }, [dotFillColor, allDots, showRegressionLine, confidenceIntervalDots]);

  useEffect(() => {
    const root = d3.select(d3Container.current);
    const drawRegressionLine = () => {

      const lrCoeffs = linearRegression(allDots);
      const lrFn = linearRegressionLine(lrCoeffs);

      const linearRegressionDots = sortBy(
        [
          (yDomain[0] - lrCoeffs.b) / lrCoeffs.m,
          (yDomain[1] - lrCoeffs.b) / lrCoeffs.m
        ]
      ).map(xValue => {
        const [firstxDomain, secondxDomain] = xDomain;
        let normalizedXValue = null;

        if (xValue < firstxDomain) {
          normalizedXValue = firstxDomain;
        } else if (xValue > secondxDomain) {
          normalizedXValue = secondxDomain;
        } else {
          normalizedXValue = xValue;
        }

        return [
          normalizedXValue,
          lrFn(normalizedXValue)
        ];
      });

      const line = d3.line()
        .x(value => x(value[0]) + canvasMargin.left + dotsMargin.left)
        .y(value => y(value[1]) + canvasMargin.top);

      root.append('path')
        .datum(linearRegressionDots)
        .attr('id', 'regressionLine')
        .attr('stroke', lineColor)
        .attr('stroke-width', '2')
        .attr('d', line);
    };

    const removeRegressionLine = () => {
      root.select('#regressionLine').remove();
    };

    removeRegressionLine();
    showRegressionLine && drawRegressionLine();
  }, [showRegressionLine, allDots]);

  useEffect(() => {
    const root = d3.select(d3Container.current);

    const drawConfidenceInterval = () => {
      const { lwrs, uprs } = confidenceIntervalDots;

      const lwrConfidenceIntervalDots = map(lwrs, lwr => [lwr.x, lwr.y]);

      const uprConfidenceIntervalDots = map(uprs, upr => [upr.x, upr.y]);

      const line = d3.line()
        .x(value => x(value[0]) + canvasMargin.left + dotsMargin.left)
        .y(value => y(value[1]) + canvasMargin.top);

      root.append('path')
        .datum(lwrConfidenceIntervalDots)
        .attr('id', 'lwrConfidenceInterval')
        .attr('stroke', lineColor)
        .attr('fill', 'none')
        .attr('stroke-dasharray', '5,2')
        .attr('d', line);

      root.append('path')
        .datum(uprConfidenceIntervalDots)
        .attr('id', 'uprConfidenceInterval')
        .attr('stroke', lineColor)
        .attr('fill', 'none')
        .attr('stroke-dasharray', '5,2')
        .attr('d', line);
    };

    const removeConfidenceIntervalLines = () => {
      root.select('#uprConfidenceInterval').remove();
      root.select('#lwrConfidenceInterval').remove();
    };

    removeConfidenceIntervalLines();
    if (confidenceIntervalDots) {
      drawConfidenceInterval();
    }
  }, [lineColor, confidenceIntervalDots]);

  return (
    <svg data-testid="scatterPlot" ref={d3Container} width={width} height={height} />
  );
};

ScatterPlotComponent.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,

  dots: PropTypes.array.isRequired,

  dotFillColor: PropTypes.string,
  lineColor: PropTypes.string,

  showRegressionLine: PropTypes.bool,

  yAxisLabel: PropTypes.string,
  xAxisLabel: PropTypes.string,

  originalDots: PropTypes.array,

  confidenceIntervalDots: PropTypes.shape({
    lwrs: PropTypes.array.isRequired,
    uprs: PropTypes.array.isRequired
  })
};

export default ScatterPlotComponent;
